import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import { Link } from '../styles';
import ModalImpacts from './ModalImpacts/ModalImpacts';
import {
  Indicator,
  IndicatorGroups,
  replaceBeneficiary,
  SelectedIndicators,
} from './ModalImpacts/GroupedIndicators';
import { SectionBase } from './SectionBase';
import { GuidanceTextResponse } from './utils';
import { INDICATOR_STAGES } from './ImpactIndicator/utils';
import { replaceQuestionText } from 'shared/utils/formatText';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { useProgram } from 'shared/providers/ProgramProvider';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';

const defaultHelpText = (
  <div style={{ textAlign: 'left' }}>
    <TrueImpactText variant="heading-subheading" color="ctaBrandOrange">
      The indicators selected here will make up your Impact Model’s{' '}
      <strong>Succeed Stage</strong>.
    </TrueImpactText>
    <TrueImpactText>
      Each indicator will need to be defined, quantified, and measured. We
      recommend including your top 1-3 metrics of success. You’re welcome to
      include more, but please note that the more you include, the longer the
      reporting process can take.
    </TrueImpactText>
  </div>
);

export interface IndicatorDetail {
  id: number; // section id
  name: string; // section name
  indicators: Indicator[];
}

interface SectionIndicatorsProps {
  title: string;
  subTitle: string;
  helpText: string;
  programName?: string;
  intermediaries?: string;
  guidanceText?: GuidanceTextResponse;
  beneficiaryTerm?: string;
  questionId: number;
  onChange: (questionId: number, value: string | string[]) => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  allIndicators: IndicatorDetail[];
  options: Indicator[];
  value: string | string[] | undefined;
  isProgramLevelQuestion?: boolean;
}

const StyledDiv = styled.div`
  .checkbox {
    margin-top: ${SPACERS.tiny};
    margin-bottom: ${SPACERS.tiny};
  }
`;

const getSelectedValues = (
  values: string | string[],
): { selectedValues: SelectedIndicators; selectedValuesArray: Indicator[] } => {
  const selectedValues: SelectedIndicators = {};
  const selectedValuesArray: Indicator[] = [];
  try {
    const val: string[] =
      typeof values === 'string'
        ? JSON.parse(values.replace(/'/g, '"'))
        : values;

    val.forEach((x, i) => {
      const indicator = { name: x, id: i };
      selectedValues[x] = indicator;
      selectedValuesArray.push(indicator);
    });
    return { selectedValues, selectedValuesArray };
  } catch (error) {
    return { selectedValues, selectedValuesArray };
  }
};

const parseAllIndicators = (
  allIndicators: IndicatorDetail[],
): IndicatorGroups => {
  const result: IndicatorGroups = {};
  allIndicators.forEach(indicators => {
    result[indicators.name] = indicators.indicators;
  });
  return result;
};

const mergeArrays = (arr1: Indicator[], arr2: Indicator[]): Indicator[] => {
  const result: Indicator[] = [];
  const index: { [key: string]: boolean | undefined } = {};
  for (let i = 0; i < arr1.length; i++) {
    const element = arr1[i];
    if (!index[element.name]) {
      index[element.name] = true;
      result.push(element);
    }
  }
  for (let i = 0; i < arr2.length; i++) {
    const element = arr2[i];
    if (!index[element.name]) {
      index[element.name] = true;
      result.push(element);
    }
  }
  return result;
};

const defaultGetLabel = (term?: string) => (value: Indicator): string =>
  replaceBeneficiary(value.name, term);

const SectionIndicators: React.FC<SectionIndicatorsProps> = ({
  title,
  subTitle,
  helpText,
  questionId,
  programName,
  intermediaries,
  beneficiaryTerm,
  onChange,
  error,
  guidanceText,
  allIndicators,
  options,
  value = [],
  isProgramLevelQuestion,
}) => {
  const { setIndicatorState } = useIndicator();
  const { answers: programAnswers } = useProgram();
  const programAnswerText = programAnswers?.[questionId]?.text;
  const seededRef = useRef(false);
  const indicators = useMemo(() => parseAllIndicators(allIndicators), [
    allIndicators,
  ]);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const { selectedValues, selectedValuesArray } = useMemo(
    () => getSelectedValues(value),
    [value],
  );

  const [selected, setSelected] = useState<SelectedIndicators>(selectedValues);

  const [shownOptions, setShownOptions] = useState<Indicator[]>(
    mergeArrays(options, selectedValuesArray),
  );

  useEffect(() => {
    const result: string[] = [];
    const selectedIndicators: Indicator[] = [];
    Object.keys(selected).forEach(x => {
      const element = selected[x];
      if (element) {
        selectedIndicators.push(element);
        result.push(x);
      }
    });
    setIndicatorState(questionId, result);
    onChange(questionId, JSON.stringify(result));
    setShownOptions(o => mergeArrays(o, selectedIndicators));
  }, [setShownOptions, setIndicatorState, selected]);

  const handleCloseModal = useCallback(() => {
    setModalIsOpen(false);
  }, [setModalIsOpen]);

  const handleChange = useCallback(
    (value: Indicator) => {
      setSelected(s => {
        const newState = { ...s };
        newState[value.name] = newState[value.name] ? undefined : value;
        return newState;
      });
    },
    [setSelected],
  );

  // One-shot auto-populate from the program-level answer when the report
  // has no answer yet. Selections stay editable.
  useEffect(() => {
    if (seededRef.current) return;
    if (!isProgramLevelQuestion) return;
    if (!programAnswerText) return;
    const currentIsEmpty = Array.isArray(value) ? value.length === 0 : !value;
    if (!currentIsEmpty) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    const seeded = getSelectedValues(programAnswerText);
    setSelected(seeded.selectedValues);
    setShownOptions(o => mergeArrays(o, seeded.selectedValuesArray));
  }, [isProgramLevelQuestion, programAnswerText, value]);

  return (
    <>
      <SectionBase
        title={title}
        subTitle={
          typeof subTitle === 'string'
            ? replaceQuestionText({
                text: subTitle,
                programName,
                primaryBeneficiary: intermediaries || beneficiaryTerm,
              })
            : subTitle
        }
        error={error}
        questionId={questionId}
        helpText={defaultHelpText}
        guidanceText={guidanceText}
      >
        <StyledDiv>
          <SelectorOptions
            variant="checkbox"
            options={shownOptions}
            getLabel={defaultGetLabel(beneficiaryTerm)}
            getSelected={val => Boolean(selected[val.name])}
            onClick={handleChange}
            tooltip={val => val.definition || ''}
          />
        </StyledDiv>
        <p onClick={() => setModalIsOpen(true)}>
          <Link>Add other impact not listed here</Link>
        </p>
      </SectionBase>
      <ModalImpacts
        open={modalIsOpen}
        onClose={handleCloseModal}
        onChange={handleChange}
        indicators={indicators}
        selected={selected}
        onlyShowStageId={INDICATOR_STAGES.succeed}
        beneficiaryTerm={beneficiaryTerm}
      />
    </>
  );
};

export default SectionIndicators;
